import { ChevronDownIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import type { FilterValue } from "@/components/filter-popover";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import type { FilterMode } from "@/pages/accounts/account-explorer/touchpoints-table/types";

interface FilterModeDropdownProps {
  filterMode: FilterMode;
  filters: Record<string, FilterValue>;
  onFilterModeChange: (mode: FilterMode) => void;
}

export function FilterModeDropdown({
  filterMode,
  filters,
  onFilterModeChange,
}: FilterModeDropdownProps) {
  const hasFilters = Object.keys(filters).length > 0;

  if (!hasFilters) {
    return null;
  }

  return (
    <div className="relative">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center justify-between gap-2 rounded-lg corner-smooth-lg border border-border bg-card text-card-foreground shadow-sm"
          >
            <div className="flex gap-2 items-center min-w-0">
              <InfoTooltip
                trigger={
                  <span className="inline-flex h-5 w-5 cursor-pointer items-center justify-center">
                    <InfoCircledIcon className="h-4 w-4 text-muted-foreground" />
                  </span>
                }
                side="bottom"
                align="start"
                className="w-80 text-sm"
              >
                <div className="space-y-2">
                  <h4 className="font-medium">Filter Mode</h4>
                  <p>
                    Choose how to display filtered results. Highlight mode will
                    emphasize matching items, while Only Show mode will display
                    only the matching items.
                  </p>
                </div>
              </InfoTooltip>
              <span className="text-sm font-medium text-primary">
                {filterMode === "highlight" ? "Highlight" : "Only Show"}
              </span>
            </div>
            <ChevronDownIcon className="h-4 w-4 opacity-50 flex-shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-[150px] rounded-lg corner-smooth-lg max-h-[300px] overflow-y-auto"
        >
          <DropdownMenuItem
            onClick={() => onFilterModeChange("highlight")}
            className="rounded-lg corner-smooth-lg"
          >
            Highlight
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => onFilterModeChange("only-show")}
            className="rounded-lg corner-smooth-lg"
          >
            Only Show
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
